// Utilitaires de calcul pour Admin → Finances : encaissements, dépenses,
// bénéfice, classement des services par rentabilité.
// Règle : une transaction compte comme "encaissée" si statut === 'payé'
// (même convention que ArchivesModal, pour rester cohérent partout).

function isPaye(t) {
  return t.statut === 'payé';
}

function inPeriod(dateStr, { day, month, year }) {
  if (!dateStr) return false;
  if (day   && dateStr !== day) return false;
  if (month && !dateStr.startsWith(month)) return false;  // month = 'YYYY-MM'
  if (year  && !dateStr.startsWith(year)) return false;    // year = 'YYYY'
  return true;
}

// Total encaissé sur une période donnée (day='YYYY-MM-DD' | month='YYYY-MM' | year='YYYY')
export function totalEncaisse(transactions, period = {}) {
  return (transactions || [])
    .filter(isPaye)
    .filter(t => inPeriod(t.date, period))
    .reduce((s, t) => s + (t.total || 0), 0);
}

// Nombre de transactions payées sur la période
export function countEncaisse(transactions, period = {}) {
  return (transactions || []).filter(isPaye).filter(t => inPeriod(t.date, period)).length;
}

// Répartition du chiffre d'affaires par "rubrique" (= libellé de chaque
// ligne facturée), sur une période donnée. Retourne un tableau trié du
// plus rentable au moins rentable : [{ categorie, montant }, ...]
export function classementServices(transactions, period = {}) {
  const totaux = {};
  (transactions || [])
    .filter(isPaye)
    .filter(t => inPeriod(t.date, period))
    .forEach(t => {
      (t.lines || []).forEach(l => {
        const cle = (l.desc || 'Autre').trim();
        totaux[cle] = (totaux[cle] || 0) + (l.qty || 1) * (l.prix || 0);
      });
    });
  return Object.entries(totaux)
    .map(([categorie, montant]) => ({ categorie, montant }))
    .sort((a, b) => b.montant - a.montant);
}

// Détail jour par jour (pour un mois donné 'YYYY-MM'), avec total par jour
export function pointJournalier(transactions, month) {
  const parJour = {};
  (transactions || [])
    .filter(isPaye)
    .filter(t => t.date && t.date.startsWith(month))
    .forEach(t => {
      parJour[t.date] = (parJour[t.date] || 0) + (t.total || 0);
    });
  return Object.entries(parJour)
    .map(([date, montant]) => ({ date, montant }))
    .sort((a, b) => a.date.localeCompare(b.date));
}

// Dépenses totales sur une période
export function totalDepenses(depenses, period = {}) {
  return (depenses || []).filter(d => inPeriod(d.date, period)).reduce((s, d) => s + (Number(d.montant) || 0), 0);
}

export function totalDepensesParCategorie(depenses, period = {}) {
  const totaux = { personnel: 0, autre: 0 };
  (depenses || []).filter(d => inPeriod(d.date, period)).forEach(d => {
    totaux[d.categorie === 'personnel' ? 'personnel' : 'autre'] += Number(d.montant) || 0;
  });
  return totaux;
}

// Bénéfice = encaissé - dépenses, sur la même période
export function benefice(transactions, depenses, period = {}) {
  return totalEncaisse(transactions, period) - totalDepenses(depenses, period);
}

// Applications "sur mesure" commandées (détecte les lignes dont le libellé
// contient "sur mesure", peu importe le type de transaction d'origine)
export function applicationsSurMesure(transactions) {
  return (transactions || []).filter(t =>
    (t.lines || []).some(l => (l.desc || '').toLowerCase().includes('sur mesure'))
    || t.prestation === 'Application sur mesure'
  );
}

export function todayStr()  { return new Date().toISOString().slice(0, 10); }
export function monthStr()  { return new Date().toISOString().slice(0, 7); }
export function yearStr()   { return new Date().toISOString().slice(0, 4); }

// ─────────────────────────────────────────────────────────────
// Répartition des rentrées par GRANDE RUBRIQUE — pour comprendre
// d'où vient réellement la rentabilité (Admin → Finances).
// Catégories fixes : Abonnement / Application sur mesure /
// Entretien-Maintenance / Bonus Marketplace. Tout le reste tombe
// dans "Autres" (Solaire, Immobilier, Forage, etc.), avec un
// sous-détail par libellé exact de chaque ligne facturée.
// ─────────────────────────────────────────────────────────────
function categoriserTransaction(t) {
  if (t.type === 'commission_marketplace') return 'marketplace';
  if (t.abonnement === true) return 'abonnement';
  const texte = (t.lines || []).map(l => l.desc || '').join(' ').toLowerCase();
  if (texte.includes('sur mesure') || t.prestation === 'Application sur mesure') return 'sur_mesure';
  if (/entretien|maintenance|support/i.test(texte)) return 'entretien';
  return 'autres';
}

export function repartitionParRubrique(transactions, period = {}) {
  const paid = (transactions || []).filter(isPaye).filter(t => inPeriod(t.date, period));

  const buckets = {
    abonnement: { label: 'Abonnements',              total: 0, count: 0 },
    sur_mesure: { label: 'Applications sur mesure',  total: 0, count: 0 },
    entretien:  { label: 'Entretien / Maintenance',  total: 0, count: 0 },
    marketplace:{ label: 'Bonus Marketplace',        total: 0, count: 0 },
    autres:     { label: 'Autres (Solaire, Immobilier, Forage...)', total: 0, count: 0, detail: {} },
  };

  paid.forEach(t => {
    const cat = categoriserTransaction(t);
    buckets[cat].total += t.total || 0;
    buckets[cat].count += 1;
    if (cat === 'autres') {
      (t.lines || []).forEach(l => {
        const libelle = (l.libelle || l.rubrique || l.desc || 'Non précisé').trim();
        buckets.autres.detail[libelle] = (buckets.autres.detail[libelle] || 0) + (l.qty || 1) * (l.prix || 0);
      });
    }
  });

  buckets.autres.detailList = Object.entries(buckets.autres.detail)
    .map(([libelle, montant]) => ({ libelle, montant }))
    .sort((a, b) => b.montant - a.montant);

  const totalGeneral = Object.values(buckets).reduce((s, b) => s + b.total, 0);

  return { buckets, totalGeneral };
}
